// Copyright (c) 2019, 2020 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import * as Firebase from "firebase";

import {
  ActivityDocument,
  OrganizationDocument,
  SupervisorDocument,
  Volunteer,
  VolunteerDocument,
  Uid,
} from "./schema";

export const FIELD_PATH = {
  schemaVersion: "version",
  dataStartDate: "data.startDate",
  dataEmail: "data.email",
  dataEndDate: "data.endDate",
  dataName: "data.name",
  dataOrganizationUid: "data.organizationUid",
  dataSchoolUid: "data.schoolUid",
  dataVolunteerUid: "data.volunteerUid",
  dataUid: "data.uid",
};

export type Firestore = Firebase.firestore.Firestore;
export type CollectionReference = Firebase.firestore.CollectionReference;
export type DocumentReference = Firebase.firestore.DocumentReference;
export type Query<T> = Firebase.firestore.Query<T>;
export type QuerySnapshot = Firebase.firestore.QuerySnapshot;
export type DocumentData = Firebase.firestore.DocumentData;

// administrators/{uid}: AdministratorDocument
// crashes/{uid}: freeform
// coordinators/{uid}: CoordinatorDocument
// schools/{uid}: SchoolDocument
// userinfos/{email}: UserInfoDocument
// volunteers/{uid}: VolunteerDocument

// schools/{schoolUid}/activities/{uid}: ActivityDocument
// schools/{schoolUid}/activity-reviews/{uid}: ActivityReviewDocument
// schools/{schoolUid}/categories/{uid}: CategoryDocument
// schools/{schoolUid}/markdowns/{uid}: MarkdownDocument
// schools/{schoolUid}/organizations/{uid}: OrganizationDocument
// schools/{schoolUid}/supervisors/{uid}: SupervisorDocument

// Imports are under email to simplify permissioning
// schools/{schoolUid}/volunteer-imports/{email}: VolunteerImportDocument
// schools/{schoolUid}/volunteer-imports/{email}/activities/{uid}: ActivityImportDocument
// schools/{schoolUid}/volunteer-imports/{email}/organizations/{uid}: OrganizationImportDocument
// schools/{schoolUid}/volunteer-imports/{email}/supervisors/{uid}: SupervisorImportDocument

const ACTIVITIES = "activities";
const ACTIVITY_REVIEWS = "activity-reviews";
const ADMINISTRATORS = "administrators";
const CATEGORIES = "categories";
const COORDINATORS = "coordinators";
const MARKDOWNS = "markdowns";
const ORGANIZATIONS = "organizations";
const SCHOOLS = "schools";
const SUPERVISORS = "supervisors";
const USERINFOS = "userinfos";
const VOLUNTEERS = "volunteers";

const IMPORTS = "volunteer-imports";

export type ColAcc0 = () => CollectionReference;
export type DocAcc0 = (uid: Uid) => DocumentReference;
export type Acc0 = [ColAcc0, DocAcc0];
export type ColAcc1 = (schoolUid: Uid) => CollectionReference;
export type DocAcc1 = (schoolUid: Uid, uid: Uid) => DocumentReference;
export type Acc1 = [ColAcc1, DocAcc1];
export type Query0<T> = (uid: Uid) => Query<T>;
export type VolunteerQuery<T> = (v: Volunteer) => Query<T>;

export interface Accessors {
  readonly userInfo: DocAcc0;
  readonly userInfos: ColAcc0;
  readonly administrator: DocAcc0;
  readonly administrators: ColAcc0;
  readonly coordinator: DocAcc0;
  readonly coordinators: ColAcc0;
  readonly volunteer: DocAcc0;
  readonly volunteers: ColAcc0;
  readonly schoolVolImport: DocAcc1;
  readonly schoolVolImports: ColAcc1;
  readonly school: DocAcc0;
  readonly schools: ColAcc0;
  readonly schoolAct: DocAcc1;
  readonly schoolActs: ColAcc1;
  readonly schoolActRvw: DocAcc1;
  readonly schoolActRvws: ColAcc1;
  readonly schoolCat: DocAcc1;
  readonly schoolCats: ColAcc1;
  readonly schoolMdown: DocAcc1;
  readonly schoolMdowns: ColAcc1;
  readonly schoolOrg: DocAcc1;
  readonly schoolOrgs: ColAcc1;
  readonly schoolSuper: DocAcc1;
  readonly schoolSupers: ColAcc1;
  readonly schoolVols: Query0<VolunteerDocument>;
  readonly volActs: VolunteerQuery<ActivityDocument>;
  readonly volOrgs: VolunteerQuery<OrganizationDocument>;
  readonly volSupers: VolunteerQuery<SupervisorDocument>;
}

export function accessors(db: Firestore): Accessors {
  const collection = (name: string) => db.collection(name);

  function accessors0(name: string): Acc0 {
    const col = () => collection(name);
    const doc = (uid: string) => col().doc(uid);
    return [col, doc];
  }

  function accessors1(acc0: Acc0, name: string): Acc1 {
    const col = (uid0: string) => acc0[1](uid0).collection(name);
    const doc = (uid0: string, uid1: string) => col(uid0).doc(uid1);
    return [col, doc];
  }

  const [userInfos, userInfo] = accessors0(USERINFOS);
  const [administrators, administrator] = accessors0(ADMINISTRATORS);
  const [coordinators, coordinator] = accessors0(COORDINATORS);
  const [volunteers, volunteer] = accessors0(VOLUNTEERS);

  const ofSchool = accessors0(SCHOOLS);
  const [schools, school] = ofSchool;

  const [schoolActs, schoolAct] = accessors1(ofSchool, ACTIVITIES);
  const [schoolActRvws, schoolActRvw] = accessors1(ofSchool, ACTIVITY_REVIEWS);
  const [schoolCats, schoolCat] = accessors1(ofSchool, CATEGORIES);
  const [schoolMdowns, schoolMdown] = accessors1(ofSchool, MARKDOWNS);
  const [schoolOrgs, schoolOrg] = accessors1(ofSchool, ORGANIZATIONS);
  const [schoolSupers, schoolSuper] = accessors1(ofSchool, SUPERVISORS);

  const ofSchoolVolImport = accessors1(ofSchool, IMPORTS);
  const [schoolVolImports, schoolVolImport] = ofSchoolVolImport;

  function schoolVols(schoolUid: Uid): Query<VolunteerDocument> {
    return volunteers().where(
      FIELD_PATH.dataSchoolUid,
      "==",
      schoolUid
    ) as Query<VolunteerDocument>;
  }

  function ofSchoolVol<T>(accessor: ColAcc1): VolunteerQuery<T> {
    return (volunteer: Volunteer) =>
      accessor(volunteer.schoolUid).where(
        FIELD_PATH.dataVolunteerUid,
        "==",
        volunteer.uid
      ) as Query<T>;
  }
  const volActs = ofSchoolVol<ActivityDocument>(schoolActs);
  const volOrgs = ofSchoolVol<OrganizationDocument>(schoolOrgs);
  const volSupers = ofSchoolVol<SupervisorDocument>(schoolSupers);

  return {
    userInfo,
    userInfos,
    administrator,
    administrators,
    coordinator,
    coordinators,
    volunteer,
    volunteers,
    schoolVolImport,
    schoolVolImports,
    school,
    schools,
    schoolAct,
    schoolActs,
    schoolActRvw,
    schoolActRvws,
    schoolCat,
    schoolCats,
    schoolMdown,
    schoolMdowns,
    schoolOrg,
    schoolOrgs,
    schoolSuper,
    schoolSupers,
    schoolVols,
    volActs,
    volOrgs,
    volSupers,
  };
}
